import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { SessionStorageService } from './core/util/session-storage.service';
import { NotificationService } from './core/components/notification/notification.service';


@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private router: Router) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean {
        if (this.authService.getIsAuthenticated().refreshToken != null) {
            return true; // Allow access to the route for users
        } else {
            this.router.navigate(['/login']); // Redirect to an login page
            return false; // Do not allow access for unidentified users
        }
    }
}


@Injectable({
    providedIn: 'root'
})
export class LoginGuard implements CanActivate {
    constructor(
        private authService: AuthService) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean {
        if (this.authService.getIsAuthenticated().refreshToken === null) {
            return true;
        } else {
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class CostingGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'costing')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class InvoiceGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        const userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        const costingRole = userRoles.roles.find((item: any) => item.role === 'invoice')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class CSAdminGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'cs_admin')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class MgtAdminGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
        let mgtAdminRole = userRoles.roles.find((item: any) => item.role === 'mgt_admin')

        if (mgtAdminRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class SalesDashboardGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'sales_dashboard')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class CustomerSalesDashboardGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'customer_sales_dashboard')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class InvoiceDashboardGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'invoice_dashboard')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class AgingDashboardGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'aging_dashboard')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class DebtorFineDashboardGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'debtor_fine_dashboard')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class OutstandingDashboardGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'outstanding_dashboard')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class ExchangeRateUpdateGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private notificationService: NotificationService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        let userRoles = this.sessionStorageService.getUserRoles()
        if (!userRoles) {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }

        let costingRole = userRoles.roles.find((item: any) => item.role === 'exchange_rate_update')

        if (costingRole) {
            return true;
        } else {
            this.notificationService.Warning("This action is prohibited for you by the system.", "Unauthorized access")
            return false;
        }
    }
}
